import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {MAX_DATA_POINTS} from 'analysis-tool/charting/AnalysisChartingConstants';
import {getStepReferencesForViewActions} from 'analysis-tool/data/DataReferenceUtils';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import isArchived from 'archive/isArchived';
import {ChartNode, Chart} from 'charts/ChartTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameTableWrapper, {
  DataFrameTableWrapperOverflowOption,
  RowSelectionProps,
} from 'data-frames/wrapper/DataFrameTableWrapper';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import PresentationalUniversalBlockView, {
  ErrorHandlingOptions,
} from 'insights/universal-blocks/PresentationalUniversalBlockView';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {PreviewChart, StepReference} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import removeNulls from 'util/removeNulls';

import {openFlagRowsModal} from '../flag-rows/FlagRowsModal';
import {getFlaggedRowsFromDataFrame} from '../flag-rows/FlagRowUtil';
import getDataFrameRenderOverrides from '../flag-rows/getDataFrameRenderOverrides';
import {getChartHasRowIndices, FlaggingButtons} from './ChartFlaggingUtils';
import getDatasetViewErrorHandlingOptions from './getDatasetViewErrorHandlingOptions';

function getOverflowOptions({
  analysis,
  onCopyClicked,
  onDeleteClicked,
  onDownloadImageClicked,
  onPromoteClicked,
  viewSteps,
}: Pick<
  VisualizationBlockProps,
  | 'analysis'
  | 'onCopyClicked'
  | 'onDeleteClicked'
  | 'onDownloadImageClicked'
  | 'onPromoteClicked'
  | 'viewSteps'
>): Array<DataFrameTableWrapperOverflowOption> {
  if (isAnalysisReadOnly(analysis)) {
    return [];
  }

  const {canEdit, message} = checkCanModifyStep(analysis, viewSteps[0].id, 'view', 'delete');
  const isAanalysisArchived = isArchived(analysis);
  const options: Array<DataFrameTableWrapperOverflowOption> = [];

  if (onCopyClicked != null) {
    options.push({
      icon: 'copy',
      onSelect: onCopyClicked,
      title: 'Copy view',
      isDisabled: isAanalysisArchived,
      tooltipTitle: isAanalysisArchived ? getArchivedTooltipMessage(analysis) : undefined,
    });
  }

  if (onDownloadImageClicked != null) {
    options.push({
      icon: 'save',
      onSelect: onDownloadImageClicked,
      title: 'Download as PNG',
    });
  }

  if (onPromoteClicked != null) {
    options.push({
      icon: 'table',
      onSelect: onPromoteClicked,
      title: 'Promote view to table',
      isDisabled: isAanalysisArchived,
      tooltipTitle: isAanalysisArchived ? getArchivedTooltipMessage(analysis) : undefined,
    });
  }

  if (onDeleteClicked != null) {
    options.push({
      icon: 'delete',
      onSelect: onDeleteClicked,
      title: 'Delete view',
      isDisabled: !canEdit,
      tooltipTitle: !canEdit ? message : undefined,
    });
  }

  return options;
}

function EditButton({
  analysis,
  onEditClicked,
  viewSteps,
}: Pick<VisualizationBlockProps, 'analysis' | 'onEditClicked' | 'viewSteps'>): JSX.Element {
  if (isAnalysisReadOnly(analysis)) {
    return <></>;
  }

  const {canEdit, message} = checkCanModifyStep(analysis, viewSteps[0].id, 'view', 'editLineage');

  return (
    <Tooltip text={message ?? 'Edit view'}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className="u-mini-spacing-left"
        disabled={!canEdit}
        icon="edit"
        onClick={onEditClicked}
        size={ButtonSize.MINI}
      />
    </Tooltip>
  );
}

function Toolbar({
  analysis,
  saveStatus,
  onCopyClicked,
  onDeleteClicked,
  onDownloadImageClicked,
  onEditClicked,
  onPromoteClicked,
  viewSteps,
  selectedRowIndices,
  dataFrame,
  allowVisualFlagging,
}: Pick<
  VisualizationBlockProps,
  | 'analysis'
  | 'saveStatus'
  | 'onCopyClicked'
  | 'onEditClicked'
  | 'onDownloadImageClicked'
  | 'onDeleteClicked'
  | 'onPromoteClicked'
  | 'viewSteps'
  | 'selectedRowIndices'
  | 'dataFrame'
> & {allowVisualFlagging: boolean}): JSX.Element {
  const overflowOptions = getOverflowOptions({
    analysis,
    onCopyClicked,
    onDeleteClicked,
    onDownloadImageClicked,
    onPromoteClicked,
    viewSteps,
  });

  // Get flagging buttons if points are selected and chart supports row indices

  return (
    <>
      <div className={classNames('u-mini-spacing-left', css(styles.saveStatus))}>{saveStatus}</div>
      <FlaggingButtons
        analysis={analysis}
        viewSteps={viewSteps}
        selectedRowIndices={selectedRowIndices}
        dataFrame={dataFrame}
        allowVisualFlagging={allowVisualFlagging}
      />
      {onEditClicked && (
        <EditButton analysis={analysis} onEditClicked={onEditClicked} viewSteps={viewSteps} />
      )}
      {overflowOptions.length > 0 && (
        <OverflowMenu
          menuProps={{dropLeft: true}}
          options={overflowOptions.map((option) => ({...option, contents: <span>{option.title}</span>}))}
        />
      )}
    </>
  );
}

function getRowSelectionProps({
  allowRowSelection,
  viewSteps,
  analysis,
  dataFrame,
}: {
  allowRowSelection?: boolean;
  analysis: PipelineGraphQL;
  viewSteps: Array<GetAnalysisPipelineStep>;
  dataFrame: DataFrame;
}): RowSelectionProps | undefined {
  if (!allowRowSelection) {
    return undefined;
  }
  const maybeStepReferences = getStepReferencesForViewActions({
    viewSteps,
    pipelineStepType: PipelineStepType.FLAG_ROWS,
  });
  if (maybeStepReferences == null) {
    return undefined;
  }
  const {precedingStep, precedingStepData} = maybeStepReferences;
  const flaggedRows = getFlaggedRowsFromDataFrame(analysis, precedingStep, dataFrame.rows);
  return {
    flaggedRows,
    onSelectRows: (selectedRowIds: Set<string>, isAddingFlag: boolean) => {
      openFlagRowsModal({
        analysis,
        selectedRowIds,
        flaggedRowIds: flaggedRows,
        isAddingFlag,
        parentStepReference: {
          stepReference: {
            stepId: precedingStep.id,
            type: StepReference.STEP_ID,
          },
          stepDataName: precedingStepData.name,
        },
      });
    },
  };
}

export interface VisualizationBlockProps {
  allowRowSelection?: boolean;
  analysis: PipelineGraphQL;
  banner: React.ReactNode;
  clientStateId: string;
  dataFrame: DataFrame;
  dataFrameId: string;
  chart?: ChartNode | Chart | PreviewChart | null;
  chartError?: ErrorHandlingOptions | null;
  renderFromChart?: boolean;
  regressionAnalyses?: Array<SuccessfulAnalysis<RegressionAnalysis>>;
  saveStatus?: JSX.Element;
  title: React.ReactNode;
  viewSteps: Array<GetAnalysisPipelineStep>;
  visualizationConfig: VisualizationConfig | null;
  warnings?: Array<string>;
  saveToResultsOverflowOption?: DataFrameTableWrapperOverflowOption | null;
  /**
   * Callback to copy the visualization. If passed, shows option to copy the view.
   *
   * The option will be hidden if the analysis is read-only, disabled if the analyis is archived.
   */
  onCopyClicked?: () => void;
  /**
   * Callback to remove the visualization's view. If passed, shows option to delete the view.
   *
   * The option will be hidden if the analysis is read-only, and disabled if the analyis is archived.
   */
  onDeleteClicked?: () => void;
  /**
   * Callback to download the visualization as a static image. If passed, shows option to download the visualization.
   */
  onDownloadImageClicked?: () => void;
  /**
   * Callback to start editing the visualization's view. If passed, shows button to edit the view.
   *
   * The button will be hidden if the analysis is read-only, disabled if the analyis is archived.
   */
  onEditClicked?: () => void;
  /**
   * Callback to promote the visualization's data to a top-level table. If passed, shows option to promote
   * the view.
   *
   * The option will be hidden if the analysis is read-only, disabled if the analyis is archived.
   */
  onPromoteClicked?: () => void;
  /**
   * Callback when multiple points are selected via lasso.
   */
  onPointsSelected?: (rowIndices: Array<number>) => void;
  /**
   * Set of selected row indices for highlighting points in the chart.
   */
  selectedRowIndices?: Set<number>;
}

/**
 * A wrapper around PresentationalUniversalBlockView and DataFrameTableWrapper for visualization views.
 */
export default function VisualizationBlock({
  allowRowSelection,
  analysis,
  banner,
  clientStateId,
  dataFrame,
  dataFrameId,
  regressionAnalyses,
  saveStatus,
  title,
  viewSteps,
  visualizationConfig,
  renderFromChart = false,
  chart,
  chartError,
  warnings,
  saveToResultsOverflowOption,
  onCopyClicked,
  onDeleteClicked,
  onDownloadImageClicked,
  onEditClicked,
  onPromoteClicked,
  onPointsSelected,
  selectedRowIndices,
}: VisualizationBlockProps): JSX.Element {
  const visualizationType = visualizationConfig == null ? null : getVisualizationType(visualizationConfig);
  const saveStatusRenderFn = saveStatus == null ? null : () => saveStatus;
  const editButtonRenderFn =
    onEditClicked == null
      ? null
      : (): JSX.Element => (
          <EditButton
            key="edit-view-button"
            analysis={analysis}
            onEditClicked={onEditClicked}
            viewSteps={viewSteps}
          />
        );

  // If we have a valid chart preview and we are rendering from chart, then we can ignore the failed state of
  // the step being viewed, as it is not relevant to the current chart settings.
  const hasValidChart = renderFromChart && chart != null && chartError == null;
  // If the step type is CHART_VIEW and the visualizationType is TABLE, then we are converting a chart to a
  // and the error should be ignored as no longer relevant.
  const isChartViewConvertingToTable =
    visualizationType === VisualizationType.TABLE &&
    viewSteps[viewSteps.length - 1].definition.stepType === PipelineStepType.CHART_VIEW;
  const errorHandlingOptions =
    getDatasetViewErrorHandlingOptions(
      viewSteps,
      !analysis.permissions.writable,
      hasValidChart || isChartViewConvertingToTable
    ) ?? chartError;

  const tableOverflowOptions = getOverflowOptions({
    analysis,
    onCopyClicked,
    onDeleteClicked,
    onDownloadImageClicked,
    onPromoteClicked,
    viewSteps,
  });
  if (saveToResultsOverflowOption != null) {
    tableOverflowOptions.push(saveToResultsOverflowOption);
  }

  return visualizationType === VisualizationType.TABLE && errorHandlingOptions == null ? (
    <DataFrameTableWrapper
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      rowSelectionProps={getRowSelectionProps({allowRowSelection, viewSteps, analysis, dataFrame})}
      additionalElements={removeNulls([saveStatusRenderFn, editButtonRenderFn])}
      additionalOverflowOptions={tableOverflowOptions}
      banner={banner}
      clientStateId={clientStateId}
      dataFrame={dataFrame}
      dataFrameId={dataFrameId}
      // TODO(BNCH-87910): Pass filter options to show filters in column headers
      // filterOptions={filterOptions}
      renderIsExcludedColumn={false}
      dataFrameRenderOverrides={getDataFrameRenderOverrides(analysis, viewSteps[viewSteps.length - 1])}
      title={title}
    />
  ) : (
    <PresentationalUniversalBlockView
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      renderFromChart={renderFromChart}
      chart={chart}
      errorHandlingOptions={errorHandlingOptions}
      inputDataFrame={dataFrame}
      dataFrameRenderOverrides={getDataFrameRenderOverrides(analysis, viewSteps[viewSteps.length - 1])}
      viewStateId={clientStateId}
      visualizationConfig={visualizationConfig}
      regressionAnalyses={regressionAnalyses}
      maxRows={MAX_DATA_POINTS}
      onPointsSelected={onPointsSelected}
      selectedRowIndices={selectedRowIndices}
      paneProps={{
        banner,
        dataTestAttrs: {component: 'VisualizationBlock', element: 'root', key: visualizationType ?? 'None'},
        title,
        toolbar: (
          <Toolbar
            analysis={analysis}
            saveStatus={saveStatus}
            viewSteps={viewSteps}
            onCopyClicked={onCopyClicked}
            onDeleteClicked={onDeleteClicked}
            onDownloadImageClicked={onDownloadImageClicked}
            onEditClicked={onEditClicked}
            onPromoteClicked={onPromoteClicked}
            selectedRowIndices={selectedRowIndices}
            dataFrame={dataFrame}
            allowVisualFlagging={getChartHasRowIndices(chart)}
          />
        ),
        warnings,
      }}
    />
  );
}

const styles = StyleSheet.create({
  saveStatus: {
    display: 'inline-flex',
  },
});
